import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useT, type MessageKey } from '../lib/i18n';
import { Brand } from './Brand';
import { QuickCapture } from './QuickCapture';
import { SearchPalette } from './SearchPalette';
import { SearchTrigger } from './SearchTrigger';
import { SessionControls } from './SessionControls';
import { TopBar } from './TopBar';

/**
 * The frame every signed-in screen sits in: an icon rail on a wide screen, a top
 * bar and a bottom tab bar below `lg`.
 *
 * **The rail is where the vision now lives.** The four modules were tiles on a home
 * screen until todos became real; naming them here as well would have left two lists
 * to keep in step, and the one that fell behind would have been the one nobody looks
 * at (D260814k). The two that are not built are on the rail, marked "Soon" and
 * unpressable — visible as work not yet done rather than as something broken.
 *
 * The rail and the tab bar are never both on screen: one is `hidden lg:flex`, the
 * other `lg:hidden`.
 */

/**
 * The four modules, as data rather than written out four times.
 *
 * `to` is what separates a module that exists from one that does not, and it is
 * the only thing that changes as each is built: an entry gains a path and loses
 * its badge.
 *
 * The `icon` is the **contents** of an `<svg viewBox="0 0 24 24">`, not the
 * element: the rail and the tab bar decide the size, and an entry supplying a
 * whole `<svg>` would eventually supply a different one.
 *
 * `satisfies` rather than an annotation checks the keys against the catalogue while
 * leaving them the literals they are, so a key that took a parameter would fail to
 * compile here instead of rendering the word `undefined` on a tab.
 */
const MODULES = [
  {
    name: 'module.todos',
    to: '/todos',
    icon: <path d="M4 7h16M4 12h16M4 17h9" />,
  },
  {
    name: 'module.reminders',
    to: '/reminders',
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
  },
  {
    name: 'module.notes',
    to: '/notes',
    icon: (
      <>
        <path d="M5 4h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
        <path d="M14 4v5h5" />
      </>
    ),
  },
  {
    name: 'module.snippets',
    to: null,
    icon: <path d="m9 8-4 4 4 4M15 8l4 4-4 4" />,
  },
] satisfies { name: MessageKey; to: string | null; icon: ReactNode }[];

type Module = (typeof MODULES)[number];

function ModuleIcon({ icon }: { icon: ReactNode }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icon}
    </svg>
  );
}

/**
 * One entry, in either navigation.
 *
 * A module that is not built is a `<button disabled>` and not a link: a target
 * that looked pressable and did nothing would be read as a fault in the
 * application. The badge says the same thing a second time, in words, because the
 * shape alone is only legible to somebody who already knows the convention.
 */
function ModuleTab({ module, compact }: { module: Module; compact: boolean }): ReactElement {
  const t = useT();
  const label = t(module.name);

  // The badge sits on its own line rather than in a corner: the rail is 68 px
  // wide, and anything positioned over the icon there covers it.
  const shape = `flex flex-col items-center justify-center gap-1 rounded-lg py-1.5 transition-colors ${
    compact ? 'min-h-14 flex-1' : 'w-full'
  }`;
  const labelShape = 'max-w-full truncate text-[10px] leading-none font-medium';

  if (!module.to) {
    return (
      <button type="button" disabled className={`${shape} text-ink-400`}>
        <ModuleIcon icon={module.icon} />
        <span className={labelShape}>{label}</span>
        <span className="rounded-full bg-accent-soft px-1.5 text-[9px] leading-4 font-medium text-accent">
          {t('nav.soon')}
        </span>
      </button>
    );
  }

  return (
    <NavLink
      to={module.to}
      className={({ isActive }) =>
        `${shape} ${
          isActive
            ? 'bg-accent-soft text-accent'
            : 'text-ink-500 hover:bg-surface hover:text-ink-900'
        }`
      }
    >
      <ModuleIcon icon={module.icon} />
      <span className={labelShape}>{label}</span>
    </NavLink>
  );
}

export function AppShell({ children }: { children: ReactNode }): ReactElement {
  const t = useT();
  const [capturing, setCapturing] = useState(false);
  const [searching, setSearching] = useState(false);

  /**
   * Three shortcuts, and two rules that apply to all of them.
   *
   * `Cmd/Ctrl+K` and `/` open search; `n` opens quick capture. The bare letters
   * are **ignored while a field has the focus**, or neither could be typed into
   * one — and the bare letters are ignored with a modifier held, so `Ctrl+N`
   * still opens whatever the browser opens. `Cmd+K` is the exception on both
   * counts: it carries a modifier, and it is the one shortcut somebody expects
   * to work from inside a field.
   *
   * A single letter is the right shape for the other two precisely because it is
   * what one reaches for without deciding to.
   */
  useEffect(() => {
    const typing = (): boolean => {
      const active = document.activeElement;
      return (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        (active instanceof HTMLElement && active.isContentEditable)
      );
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setSearching(true);
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || typing()) return;

      if (event.key === '/') {
        event.preventDefault();
        setSearching(true);
      }
      if (event.key === 'n') {
        event.preventDefault();
        setCapturing(true);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-full lg:flex">
      {/* The rail. 68 px is the width of an icon, its label and the padding
          around them — wide enough to read, narrow enough that the list still
          has the page to itself. */}
      <nav
        aria-label={t('nav.modules')}
        className="fixed inset-y-0 left-0 z-30 hidden w-[68px] flex-col items-center gap-1 border-r border-line bg-paper/90 px-1.5 py-4 backdrop-blur-md lg:flex"
      >
        <Brand name={false} className="mb-2 justify-center" />
        <SearchTrigger onOpen={() => setSearching(true)} className="mb-1 size-10" />
        <CaptureButton onClick={() => setCapturing(true)} />
        {MODULES.map((module) => (
          <ModuleTab key={module.name} module={module} compact={false} />
        ))}
        {/* Pushed to the foot: the way out is not something to reach for while
            reading a list, and the modules are. */}
        <div className="mt-auto pt-2">
          <SessionControls orientation="vertical" />
        </div>
      </nav>

      <div className="flex min-h-full w-full flex-col lg:pl-[68px]">
        <TopBar onCapture={() => setCapturing(true)} onSearch={() => setSearching(true)} />

        {/* The bottom padding is for the tab bar, which floats over the page:
            without it the last row of a list would sit underneath it and could
            never be reached. */}
        <main className="mx-auto w-full max-w-3xl flex-1 px-5 pt-6 pb-28 sm:px-8 lg:pb-14">
          {children}
        </main>

        {/* The tab bar, below `lg`. `pb-[env(safe-area-inset-bottom)]` keeps it
            clear of the home indicator on a phone, where the application is
            installed and there is no browser chrome to sit above. */}
        <nav
          aria-label={t('nav.modules')}
          className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-paper/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden"
        >
          <div className="mx-auto flex max-w-3xl items-stretch gap-1 px-2 py-1.5">
            {MODULES.map((module) => (
              <ModuleTab key={module.name} module={module} compact />
            ))}
          </div>
        </nav>
      </div>

      {/* Mounted once, whatever screen is showing, and mounted even when it is
          closed: it owns the offline buffer and has to keep replaying it. */}
      <QuickCapture open={capturing} onClose={() => setCapturing(false)} />
      <SearchPalette open={searching} onClose={() => setSearching(false)} />
    </div>
  );
}

/**
 * The way into quick capture from the rail.
 *
 * Above the modules rather than among them, because it is not one: it is the
 * thing you press instead of choosing a module, and putting it in the list would
 * make writing a note look like a fifth place to be.
 */
function CaptureButton({ onClick }: { onClick: () => void }): ReactElement {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('notes.capture')}
      title={t('notes.captureShortcut')}
      className="mb-1 flex size-10 items-center justify-center rounded-lg bg-accent-soft text-accent transition-colors hover:bg-accent hover:text-accent-ink"
    >
      <svg
        viewBox="0 0 24 24"
        className="size-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 5v14M5 12h14" />
      </svg>
    </button>
  );
}
